/**
 * Lot 155 (D20 points 4 et 5) — rangées défilantes : flèches, clavier ←/→,
 * `ResizeObserver` et glissement à la souris.
 *
 * Rapatrie ici la logique qui vivait en `<script>` inline dans
 * `home-shop-grid.liquid` (lignes 351-416 avant ce lot) : une seule
 * implémentation pour les trois rayons de l'accueil ET la rangée des
 * compléments de `product-extras.liquid`, plutôt que deux scripts qui
 * font la même chose sur deux sections différentes (choix justifié au
 * CHANGELOG V60). Chargé une fois par page depuis `layout/theme.liquid`,
 * gardé sur `index` et `product`.
 *
 * Convention d'attributs, partagée par les deux sections :
 *   [data-sbc-carousel-group]    conteneur d'une rangée (flèches + piste)
 *   [data-sbc-carousel-track]    la piste défilante (overflow-x:auto)
 *   [data-sbc-carousel-controls] le conteneur des deux flèches
 *   [data-sbc-carousel-prev/next] les deux boutons flèche
 *   [data-sbc-glisser]           piste où la souris peut glisser le défilement
 *
 * Glissement : patron pointerdown / setPointerCapture / pointermove repris
 * de `sbc-marquee.js` (glissement de la marquee du hero) — souris
 * uniquement (`pointerType === 'mouse'`), le tactile garde son défilement
 * natif. Le clic est annulé au-delà de ~6 px de déplacement : les cartes
 * sont des liens, un glissement ne doit jamais ouvrir une fiche.
 */
(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Flèches, clavier, ResizeObserver — un groupe par rangée.
  document.querySelectorAll('[data-sbc-carousel-group]').forEach((group) => {
    if (group.dataset.sbcCarouselReady === 'true') return;
    const track = group.querySelector('[data-sbc-carousel-track]');
    const controls = group.querySelector('[data-sbc-carousel-controls]');
    const previous = group.querySelector('[data-sbc-carousel-prev]');
    const next = group.querySelector('[data-sbc-carousel-next]');
    if (!track || !controls || !previous || !next) return;
    group.dataset.sbcCarouselReady = 'true';

    const getMaxScroll = () => Math.max(0, track.scrollWidth - track.clientWidth);
    const getScrollAmount = () => Math.max(160, track.clientWidth * 0.82);

    const updateControls = () => {
      const maxScroll = getMaxScroll();
      const canScroll = maxScroll > 3;
      controls.hidden = !canScroll;
      previous.disabled = !canScroll || track.scrollLeft <= 3;
      next.disabled = !canScroll || track.scrollLeft >= maxScroll - 3;
    };

    const move = (direction) => {
      track.scrollBy({
        left: direction * getScrollAmount(),
        behavior: reducedMotion ? 'auto' : 'smooth'
      });
    };

    previous.addEventListener('click', () => move(-1));
    next.addEventListener('click', () => move(1));

    track.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        move(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        move(1);
      }
    });

    let scrollFrame = 0;
    track.addEventListener('scroll', () => {
      window.cancelAnimationFrame(scrollFrame);
      scrollFrame = window.requestAnimationFrame(updateControls);
    }, { passive: true });

    if ('ResizeObserver' in window) {
      new ResizeObserver(updateControls).observe(track);
    } else {
      window.addEventListener('resize', updateControls, { passive: true });
    }

    track.addEventListener('sbc:carousel-content-changed', updateControls);
    updateControls();
  });

  // Glissement à la souris — un seuil de 6 px avant de considérer que c'est
  // un glissement (et non un simple clic sur la carte).
  const SEUIL = 6;

  document.querySelectorAll('[data-sbc-glisser]').forEach((track) => {
    if (track.dataset.sbcGlisserReady === 'true') return;
    track.dataset.sbcGlisserReady = 'true';

    let etat = null;
    let avaleClic = null;

    // Lot 160 — LE point qui empêchait la rangée de se glisser à la VRAIE
    // souris. Les cartes sont des liens contenant une image : appuyer et
    // déplacer une vraie souris dessus démarre le glisser-déposer natif du
    // navigateur (`dragstart`), qui envoie aussitôt `pointercancel` ; l'état
    // repartait à zéro et la piste ne bougeait pas d'un pixel. Mesuré avec
    // l'outil `drag` (souris pilotée par CDP) sur la fiche et sur l'accueil :
    // journal `pointerdown` → `dragstart` → `pointercancel`, `scrollLeft`
    // inchangé à 0. Des `PointerEvent` synthétiques ne déclenchent PAS ce
    // glisser-déposer : c'est ce qui avait rendu le contrôle du lot 155 vert
    // alors que le geste réel ne marchait pas.
    track.addEventListener('dragstart', (e) => e.preventDefault());

    const desarmerAvaleClic = () => {
      if (!avaleClic) return;
      track.removeEventListener('click', avaleClic, true);
      avaleClic = null;
    };

    track.addEventListener('pointerdown', (e) => {
      desarmerAvaleClic();
      if (e.pointerType !== 'mouse' || e.button > 0) return;
      etat = { x: e.clientX, left: track.scrollLeft, dragged: false };
    });

    track.addEventListener('pointermove', (e) => {
      if (!etat) return;
      if (e.buttons === 0) {
        // Relâchement manqué (bouton lâché hors de la piste) : on remet
        // l'état à zéro sans avaler de clic, puisqu'aucun ne suivra.
        etat = null;
        track.classList.remove('is-glisse');
        track.style.scrollSnapType = '';
        track.style.scrollBehavior = '';
        return;
      }
      const dx = e.clientX - etat.x;
      if (!etat.dragged) {
        if (Math.abs(dx) < SEUIL) return;
        etat.dragged = true;
        // La capture de pointeur se prend ICI, une fois le glissement
        // reconnu, et non au `pointerdown` comme au lot 155 : une capture
        // posée dès l'appui recible sur la piste les évènements souris de
        // compatibilité, `click` compris, et plus aucun clic souris
        // n'atteignait sa vraie cible dans la piste — cartes produit des
        // rangées de l'accueil, compléments de la fiche, bouton de lecture
        // de la rubrique vidéo. Mesuré au lot 159 (clic MCP : cible
        // `UL.sbc-videos__track` au lieu du bouton ; capture neutralisée,
        // cible correcte et lecteur ouvert).
        try { track.setPointerCapture(e.pointerId); } catch (err) {}
        track.classList.add('is-glisse');
        track.style.scrollSnapType = 'none';
        // Les trois pistes portent `scroll-behavior:smooth` (le confort des
        // flèches) : sans cette bascule, chaque `scrollLeft = …` ci-dessous
        // lance une ANIMATION vers la position demandée au lieu de suivre la
        // main, et la piste traîne derrière le curseur. Rendue à chaque
        // sortie, comme `scrollSnapType`.
        track.style.scrollBehavior = 'auto';
      }
      track.scrollLeft = etat.left - dx;
    });

    const relacher = (e) => {
      if (!etat) return;
      const dragged = etat.dragged;
      etat = null;
      track.classList.remove('is-glisse');
      track.style.scrollSnapType = '';
      track.style.scrollBehavior = '';

      // Un vrai relâchement produit un "click" juste après ; un pointercancel
      // jamais — l'armer quand même laissait l'écouteur avaler le PROCHAIN
      // clic, sans rapport (clôture de la vague 151-159).
      if (dragged && e.type === 'pointerup') {
        avaleClic = (evt) => {
          evt.preventDefault();
          evt.stopPropagation();
          desarmerAvaleClic();
        };
        track.addEventListener('click', avaleClic, { capture: true });
        window.setTimeout(desarmerAvaleClic, 0);
      }
    };
    track.addEventListener('pointerup', relacher);
    track.addEventListener('pointercancel', relacher);
  });
})();
